import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";

export const isCounterUser = (user) => user?.role === "COUNTER_USER";

export function useCounterShiftMeta() {
  const [meta, setMeta] = useState({ counters: [], shifts: [], temples: [] });
  useEffect(() => {
    api.get("/meta/form-data").then((r) => setMeta({ counters: r.data.counters || [], shifts: r.data.shifts || [], temples: r.data.temples || [] })).catch(() => {});
  }, []);
  return meta;
}

// " · Counter: X · Shift: Y" for report titles; counter users always see their own counter.
export function scopeLabel(user, meta, f) {
  const cid = isCounterUser(user) ? user.counter_id : f.counter_id;
  const counter = meta.counters.find((c) => c.id === cid);
  const shift = meta.shifts.find((s) => s.id === f.shift_id);
  return [counter && `Counter: ${counter.name}`, shift && `Shift: ${shift.name}`].filter(Boolean).map((x) => ` · ${x}`).join("");
}

// Super Admin sees every temple's shifts / counters (each temple has its own "Shift 1"): keep only the chosen
// temple's, and name the temple when more than one is listed, so the same name never shows twice.
export function templeOptions(list, templeId, temples = []) {
  const rows = templeId ? list.filter((x) => !x.temple_id || x.temple_id === templeId) : list;
  const many = new Set(rows.map((x) => x.temple_id)).size > 1;
  const code = (tid) => (temples.find((t) => t.temple_id === tid) || {}).temple_code || tid;
  return rows.map((x) => ({ ...x, suffix: many && x.temple_id ? ` · ${code(x.temple_id)}` : "" }));
}

// Shift picker for everyone; counter picker for admins/supervisors. Counter users are
// limited to their assigned counter by the backend, so they see it as a fixed label.
export default function CounterShiftFilter({ user, meta, value, onChange, testid = "filter" }) {
  const set = (k) => (v) => onChange({ ...value, [k]: v === "ALL" ? "" : v });
  const shifts = templeOptions(meta.shifts, value.temple_id, meta.temples);
  const counters = templeOptions(meta.counters, value.temple_id, meta.temples);
  const own = meta.counters.find((c) => c.id === user?.counter_id);
  return (
    <>
      <div><Label className="text-xs">Shift</Label>
        <Select value={value.shift_id || ""} onValueChange={set("shift_id")}>
          <SelectTrigger data-testid={`${testid}-shift`}><SelectValue placeholder="All shifts" /></SelectTrigger>
          <SelectContent><SelectItem value="ALL">All shifts</SelectItem>
            {shifts.map((s) => <SelectItem key={s.id} value={s.id}>{s.name} ({s.start}–{s.end}){s.suffix}</SelectItem>)}</SelectContent>
        </Select></div>
      {isCounterUser(user) ? (
        <div><Label className="text-xs">Counter</Label>
          <p className="h-10 flex items-center px-3 text-sm rounded-md border border-[#E5D9C5] bg-[#FBF7EF] text-maroon-900" data-testid={`${testid}-own-counter`}>
            {own ? own.name : "My transactions"}
          </p></div>
      ) : (
        <div><Label className="text-xs">Counter</Label>
          <Select value={value.counter_id || ""} onValueChange={set("counter_id")}>
            <SelectTrigger data-testid={`${testid}-counter`}><SelectValue placeholder="All counters" /></SelectTrigger>
            <SelectContent><SelectItem value="ALL">All counters</SelectItem>
              {counters.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}{c.suffix}</SelectItem>)}</SelectContent>
          </Select></div>
      )}
    </>
  );
}
